<template>
  <div class="form-container">
    <div class="form-header">
      <h2>Edit Member Roles</h2>
    </div>
    <LoadingIndicator v-if="isLoadingMembers">Loading member details...</LoadingIndicator>
    <div v-else-if="!member" class="loading-indicator">Member not found.</div>
    <form v-else @submit.prevent="handleUpdateRoles" class="form">
      <div class="details-section">
        <h3>Member Information</h3>
        <div class="details-group">
          <label>Name</label>
          <p class="details-value">{{ member.user_first_name }} {{ member.user_last_name }}</p>
          <label>Email</label>
          <p class="details-value">{{ member.user_email }}</p>
        </div>
      </div>

      <div class="form-section">
        <h3>Roles & Permissions</h3>
        <div 
          class="form-group" 
          v-if="canEditUsers(route.params.organization_id)"
        >
          <label>Organization Roles</label>
          <LoadingIndicator v-if="isLoadingRoles">Loading roles...</LoadingIndicator>
          <div v-else class="tag-input-container" @click="focusInput">
            <div class="selected-tags">
              <span v-for="roleName in selectedRoles" :key="roleName" class="tag" :class="roleName.toLowerCase().replace(/\s+/g, '-')">
                {{ roleName }}
                <button
                  type="button"
                  class="remove-tag"
                  @click.stop="removeRole(roleName)"
                  v-if="!isProtectedRole(roleName)"
                >
                  &times;
                </button>
              </span>
            </div>
            <input
              ref="roleInput"
              v-model="searchText"
              type="text"
              class="tag-input"
              placeholder="Type to search roles..."
              @input="onSearchInput"
              @focus="showSuggestions = true"
              @keydown.backspace="handleBackspace"
              @keydown.enter.prevent="handleEnter"
            >
            <div v-if="showSuggestions && filteredRoles.length > 0" class="suggestions-dropdown">
              <div
                v-for="role in filteredRoles"
                :key="role.name"
                class="suggestion-item"
                :class="{ active: role.name === activeRole }"
                @mousedown.prevent="addRole(role.name)"
              >
                {{ role.name }}
              </div>
            </div>
          </div>
        </div>

        <div class="form-group">
          <label>Inherited Permissions</label>
          <LoadingIndicator v-if="isLoadingPermissions || isLoadingRoles">Loading permissions...</LoadingIndicator>
          <div v-else class="list-container">
            <table>
              <thead>
                <tr>
                  <th>Permission</th>
                  <th>Description</th>
                  <th>Inherited From</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="permission in inheritedPermissions" :key="permission.name">
                  <td>
                    <span class="permission-tag">{{ permission.name }}</span>
                  </td>
                  <td>{{ permission.description }}</td>
                  <td>
                    <div class="inherited-roles">
                      <span 
                        v-for="roleName in permission.inheritedFrom" 
                        :key="roleName"
                        class="tag mini"
                        :class="roleName.toLowerCase().replace(/\s+/g, '-')"
                      >
                        {{ roleName }}
                      </span>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div class="form-actions">
        <button
          type="button"
          class="delete-button big"
          @click="handleRemoveMember"
          v-if="canRemoveUser(route.params.organization_id)"
        >
          Remove from Organization
        </button>
        <button
          v-if="canEditUsers(route.params.organization_id)"
          type="submit"
          class="submit-button big"
        >
          Update Member Roles
        </button>
        <button type="button" class="cancel-button medium" @click="handleCancel">
          Cancel
        </button>
      </div>
    </form>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/vue-query';
import { isSuperuser, canEditUsers, canRemoveUser, isProtectedRole } from '@/scripts/permissions';
import { handleApiError } from '@/scripts/shared-utils';
import { getOrganizationMembers, updateMemberRoles, removeOrganizationMember, organizationKeys } from '@/scripts/api/organizationApi';
import { getRoles, getRolePermissions, roleKeys } from '@/scripts/api/rolesApi';
import { getPermissions, permissionKeys } from '@/scripts/api/permissionsApi';
import { updateUserInfo } from '@/scripts/services/authService';
import LoadingIndicator from '@/components/LoadingIndicator.vue';

const route = useRoute();
const router = useRouter();
const queryClient = useQueryClient();

const organizationId = computed(() => route.params.organization_id);
const userId = computed(() => route.params.user_id);

const selectedRoles = ref([]);
const searchText = ref('');
const showSuggestions = ref(false);
const activeRole = ref('');
const roleInput = ref(null);
const rolePermissions = ref(new Map());

// DATA FETCHING
const { data: members, isLoading: isLoadingMembers } = useQuery({
  queryKey: computed(() => organizationKeys.getOrganizationMembers(organizationId.value)),
  queryFn: () => getOrganizationMembers(organizationId.value),
  enabled: !!organizationId.value,
  staleTime: 60000,
  placeholderData: [],
  onError: (error) => handleApiError(error, 'fetching organization members')
});

const { data: roles, isLoading: isLoadingRoles } = useQuery({
  queryKey: roleKeys.getRoles(),
  queryFn: getRoles,
  staleTime: 60000,
  placeholderData: [],
  onError: (error) => handleApiError(error, 'fetching roles')
});
const { data: permissions, isLoading: isLoadingPermissions } = useQuery({
  queryKey: permissionKeys.getPermissions(),
  queryFn: getPermissions,
  staleTime: 60000,
  placeholderData: [],
  onError: (error) => handleApiError(error, 'fetching permissions')
});

const member = computed(() => {
  const userMembers = members.value.filter(m => m.user_id === userId.value);
  if (userMembers.length === 0) return null;

  return userMembers.reduce((acc, curr) => {
    if (!acc) {
      acc = { ...curr, roles: [] };
    }
    if (curr.organization_role_id && curr.role_name) {
      acc.roles.push({ id: curr.organization_role_id, name: curr.role_name });
    }
    return acc;
  }, null);
});

const sortedRoles = computed(() => roles.value.slice().sort((a, b) => a.name.localeCompare(b.name)));

const filteredRoles = computed(() => {
  const search = searchText.value.toLowerCase();
  return sortedRoles.value.filter(role => 
    !selectedRoles.value.includes(role.name) &&
    !isProtectedRole(role.name) &&
    (search ? role.name.toLowerCase().includes(search) : true)
  );
});

const inheritedPermissions = computed(() => {
  const allPermissions = new Map();
  selectedRoles.value.forEach(roleName => {
    const rolePerms = rolePermissions.value.get(roleName) || new Set();
    rolePerms.forEach(permName => {
      const permission = permissions.value.find(p => p.name === permName);
      if (permission) {
        if (!allPermissions.has(permName)) {
          allPermissions.set(permName, { ...permission, inheritedFrom: [roleName] });
        } else {
          allPermissions.get(permName).inheritedFrom.push(roleName);
        }
      }
    });
  });
  return Array.from(allPermissions.values());
});

// MUTATIONS
const { mutate: updateRoles } = useMutation({
  mutationFn: () => updateMemberRoles(organizationId.value, userId.value, selectedRoles.value),
  onSuccess: async () => {
    // in case we remove a permission to ourselves
    await updateUserInfo();
    await queryClient.invalidateQueries({ queryKey: organizationKeys.getOrganizationMembers(organizationId.value), refetchType: 'all' });
    goBack();
  },
  onError: (err) => handleApiError(err, 'updating member roles'),
});

const { mutate: removeUser } = useMutation({
  mutationFn: () => removeOrganizationMember(organizationId.value, userId.value),
  onSuccess: async () => {
    // in case we remove ourselves
    await updateUserInfo();
    await queryClient.invalidateQueries({ queryKey: organizationKeys.getOrganizationMembers(organizationId.value), refetchType: 'all' });
    goBack();
  },
  onError: (err) => handleApiError(err, 'removing member'),
});

// METHODS
const handleUpdateRoles = () => selectedRoles.value.length && updateRoles();
const handleRemoveMember = () => confirm('Are you sure?') && removeUser();
const handleCancel = () => router.back();

const goBack = () => {
  router.push(isSuperuser() ? `/organizations/${organizationId.value}/edit` : '/organization');
};

const focusInput = () => roleInput.value?.focus();
const onSearchInput = () => {
  showSuggestions.value = true;
  if (filteredRoles.value.length > 0) activeRole.value = filteredRoles.value[0].name;
};
const handleBackspace = () => !searchText.value && selectedRoles.value.length > 0 && removeRole(selectedRoles.value.at(-1));
const handleEnter = () => activeRole.value && filteredRoles.value.length > 0 && addRole(activeRole.value);

const addRole = (roleName) => {
  if (!selectedRoles.value.includes(roleName) && !isProtectedRole(roleName)) {
    selectedRoles.value.push(roleName);
  }
  searchText.value = '';
  showSuggestions.value = false;
};

const removeRole = (roleName) => {
  if (!isProtectedRole(roleName)) {
    selectedRoles.value = selectedRoles.value.filter(r => r !== roleName);
  }
};

const loadRolePermissions = async () => {
  if (!roles.value) return;
  for (const role of roles.value) {
    try {
      const permissions = await getRolePermissions(role.id);
      rolePermissions.value.set(role.name, new Set(permissions.map(p => p.name)));
    } catch (error) {
      handleApiError(error, `loading permissions for role ${role.name}`);
    }
  }
};

watch(member, (newMember) => {
  if (newMember) {
    selectedRoles.value = newMember.roles.map(role => role.name);
  }
}, { immediate: true });

watch(roles, (newRoles) => {
  if (newRoles?.length > 0) {
    loadRolePermissions();
  }
}, { immediate: true });
</script>

<style scoped>
@import '@/styles/shared-form.css';
@import '@/styles/shared-list.css';
@import '@/styles/shared-details.css';
@import '@/styles/organization-members.css';
</style>
